import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import {
  Avatar,
  Body2,
  Button,
  CancelCrossIcon,
  Chip,
  Dialog,
  Header2,
  IconButton,
  Link,
  ListSeparator,
  Tooltip,
} from "@activecollab/components";
import classnames from "classnames";
import PropTypes from "prop-types";
import { useChatControlContext } from "~/components/Chat/context";
import { ConversationResolver } from "~/components/Chat/Hub/List/ConversationResolver";
import { NotificationsCounter } from "~/components/Chat/Hub/NotificationsCounter";
import { UserOnlineStatus } from "~/components/Chat/Hub/UserOnlineStatus";
import FormatDate from "~/components/Formatters/FormatDate";
import { UserRatesAndCapacityDialog } from "~/components/Settings/UserRatesAndCapacityDialog";
import { useUserCardContext } from "~/components/UserCard/context";
import { UserWeeklyCapacity } from "~/components/Users/UserWeeklyCapacity";
import { fetchUserWorkweek } from "~/store/actions/actions.userWorkweeks";
import { getConfigOption, getIsChatEnabled } from "~/store/selectors/selector.configOptions";
import { makeGetCountTotalUnreadMessagesByConversationSelector } from "~/store/selectors/selector.conversationMessages";
import { getOneToOneConversationIdByUserId } from "~/store/selectors/selector.conversations";
import { makeGetTeamsByUserId } from "~/store/selectors/selector.teams";
import { makeGetUserConversationSelector } from "~/store/selectors/selector.userConversations";
import { getUserById, canAccessPeopleSection, getLoggedUser } from "~/store/selectors/selector.users";
import { getUserWorkweekById } from "~/store/selectors/selector.weeklyWorkerCapacity";
import Angie from "~/utils/angie";
import { isArchived, isFinancialManager, isPowerUser, isVirtual, resolvedUserDetails } from "~/utils/userHelper";
import { replaceAvatarSize } from "~/utils/utils";

const UserCard = ({ id }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { setUserId } = useUserCardContext();
  const { setConversationId } = useChatControlContext();
  const user = useSelector((state) => getUserById(state, id));
  const getTeamsByUserId = makeGetTeamsByUserId();
  const userTeams = useSelector((state) => getTeamsByUserId(state, id));
  const isChatEnabled = useSelector(getIsChatEnabled);
  const accessPeopleSection = useSelector(canAccessPeopleSection);
  const conversationId = useSelector((state) => getOneToOneConversationIdByUserId(state, id));
  const getNotificationsCounter = useMemo(
    () => makeGetCountTotalUnreadMessagesByConversationSelector(conversationId),
    [conversationId]
  );
  const counter = useSelector((state) => getNotificationsCounter(state));
  const getUserConversation = useMemo(() => makeGetUserConversationSelector(conversationId), [conversationId]);
  const userConversation = useSelector(getUserConversation);
  const { is_muted = false, is_original_muted = false } = userConversation ?? {};
  const isMuted = is_muted || is_original_muted;
  const availabilityEnabled = useSelector((state) => getConfigOption(state, `availability_enabled`));
  const workloadEnabled = useSelector((state) => getConfigOption(state, `workload_enabled`));

  const loggedUser = useSelector((state) => getLoggedUser(state));
  const isUserPowerUser = isPowerUser(loggedUser);
  const isUserFinancialManager = isFinancialManager(loggedUser);
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const workWeekData = useSelector((state) => getUserWorkweekById(state, id));

  const onCancel = useCallback(() => {
    setUserId(null);
  }, [setUserId]);

  const onButtonClick = useCallback(
    (id) => {
      setUserId(null);
      if (!isArchived(user)) {
        return setConversationId(id);
      }
      window.location.href = `mailto:${user?.email}`;
    },
    [setConversationId, setUserId, user]
  );

  const openDialog = useCallback(() => {
    setOpenUserDialog(true);
  }, []);

  const resolveTeams = useMemo(() => {
    if (userTeams.length === 1) return userTeams[0];
    const more = userTeams.length - 1;
    return t("{{value}} + {{number}} more", {
      value: userTeams[0],
      number: more,
    });
  }, [t, userTeams]);

  const canSeeCapacity = useMemo(
    () => (availabilityEnabled || workloadEnabled) && (isUserFinancialManager || isUserPowerUser),
    [availabilityEnabled, isUserFinancialManager, isUserPowerUser, workloadEnabled]
  );
  const resolvedUser = useMemo(() => resolvedUserDetails(user), [user]);

  useEffect(() => {
    if (canSeeCapacity) {
      dispatch(fetchUserWorkweek(id));
    }
  }, [canSeeCapacity, dispatch, id]);

  const isInteractive = useMemo(() => isUserFinancialManager && !user.isClient(), [isUserFinancialManager, user]);

  const showCapacity = useMemo(
    () => canSeeCapacity && (user?.class !== "Client" || user.isClientPlus()) && !isArchived(user),
    [canSeeCapacity, user]
  );

  const resolvedMembership = useMemo(() => {
    let status = t("Joined");
    if (isArchived(user)) {
      status = t("Active");
    }
    if (isVirtual(user)) {
      status = t("Created");
    }

    return (
      <>
        {status}: <FormatDate value={user?.created_on} />
        {isArchived(user) && (
          <>
            {" "}
            - <FormatDate value={user?.archived_on} />
          </>
        )}
      </>
    );
  }, [t, user]);

  const resolvedEmails = useMemo(() => {
    if (isVirtual(user)) {
      return "";
    }
    const additionalAddresses = user?.additional_email_addresses
      ? Array.isArray(user?.additional_email_addresses)
        ? user?.additional_email_addresses
        : user?.additional_email_addresses?.toArray()
      : [];
    if (additionalAddresses.length === 0) {
      return `${user.email}`;
    }

    return t("{{value}} + {{number}} more", {
      value: user.email,
      number: additionalAddresses.length,
    });
  }, [t, user]);

  return (
    <>
      <Dialog
        open={!!id}
        onClose={onCancel}
        enableBackgroundClick
      >
        <Dialog.Content>
          <div className="tw-flex">
            {!isArchived(user) && !isVirtual(user) ? (
              <UserOnlineStatus
                id={id}
                isUserCard
              >
                <Avatar
                  url={replaceAvatarSize(user?.avatar_url, 80)}
                  size={80}
                />
              </UserOnlineStatus>
            ) : (
              <Avatar
                url={replaceAvatarSize(user?.avatar_url, 80)}
                size={80}
              />
            )}
            <div className="tw-flex tw-flex-col tw-ml-6 tw-justify-center tw-overflow-hidden">
              <Header2
                className="tw-mr-6"
                whitespace="no-wrap"
                overflow="truncate"
              >
                <div className="tw-flex tw-items-center">
                  <div className="tw-truncate">{user.display_name}</div>
                  {isArchived(user) || isVirtual(user) ? (
                    <Tooltip
                      disable={!resolvedUser.tooltip}
                      title={resolvedUser.tooltip}
                    >
                      <Chip
                        label={resolvedUser.role}
                        backgroundColor={resolvedUser.color}
                        color="var(--only-black)"
                        variant="Caption 1"
                        className={classnames("tw-capitalize tw-shrink-0 tw-rounded-full tw-px-3 md:tw-ml-2", {
                          "tw-cursor-help": resolvedUser.tooltip,
                        })}
                        size="small"
                      />
                    </Tooltip>
                  ) : null}
                  {showCapacity ? (
                    <UserWeeklyCapacity
                      workWeekData={workWeekData ?? {}}
                      onClick={isInteractive ? openDialog : null}
                    />
                  ) : null}
                </div>
              </Header2>
              {!isVirtual(user) ? <Body2 color="tertiary">{resolvedEmails}</Body2> : null}
              <Body2 color="tertiary">{resolvedMembership}</Body2>
              {userTeams.length > 0 && !isArchived(user) ? (
                <Body2 color="tertiary">
                  {t("Team")}: {resolveTeams}
                </Body2>
              ) : null}
            </div>
            <IconButton
              className="tw-absolute tw-top-4 tw-right-4"
              variant="text gray"
              onClick={onCancel}
            >
              <CancelCrossIcon />
            </IconButton>
          </div>
          <ListSeparator className="tw-mx-0 tw-my-6" />
          {isChatEnabled ? (
            <div className="tw-flex tw-items-center">
              <ConversationResolver
                type="user"
                id={id}
              >
                <Button
                  className="tw-mr-6 tw-relative"
                  onClick={onButtonClick}
                >
                  {!isArchived(user) ? t("Send message") : t("Send Email")}
                  {counter > 0 && !isArchived(user) ? (
                    <NotificationsCounter
                      counter={counter}
                      className={classnames({
                        "notification-counter-muted": isMuted,
                      })}
                    />
                  ) : null}
                </Button>
              </ConversationResolver>
              {accessPeopleSection && (
                <Link
                  to={`/users/${id}`}
                  as={RouterLink}
                  onClick={onCancel}
                  variant="text link-primary"
                  className="tw-flex tw-items-center"
                >
                  {t("See full profile")}
                </Link>
              )}
            </div>
          ) : (
            <Link
              onClick={onCancel}
              href={`${Angie.activecollab_url}${user.url_path}`}
              target="_blank"
              rel="noreferrer nofollow"
            >
              {t("See profile")}
            </Link>
          )}
        </Dialog.Content>
      </Dialog>
      {openUserDialog ? (
        <UserRatesAndCapacityDialog
          userId={id}
          isOpen={openUserDialog}
          workWeekData={workWeekData}
          close={() => {
            setOpenUserDialog(false);
            setUserId(null);
          }}
        />
      ) : null}
    </>
  );
};

UserCard.propTypes = {
  id: PropTypes.number,
};

UserCard.displayName = "UserCard";

export default UserCard;
